import { createContext, useContext } from 'react';
import { Firestore, addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';

import { EtfApiLog, EtfData, EtfSection, Fund, PositionDetails } from '../../types';

//shared by every tab of one ETF (provided by etf_fund.tsx)
export interface EtfContext {
    fund: Fund;
    position: PositionDetails | null; //live funds/{id}/private/position
    data: EtfData | null; //PCF / basket / holdings rebuilt by getEtfData whenever the position changes
    loading: boolean;
    error: string | null;
}

export const EtfContextValue = createContext<EtfContext | null>(null);

export const useEtfContext = (): EtfContext => {
    const ctx = useContext(EtfContextValue);
    if (!ctx) throw new Error('useEtfContext must be used inside EtfFund');
    return ctx;
};

export const isEtfSectionError = <T,>(section: EtfSection<T> | undefined | null): section is { error: string } =>
    !!section && typeof (section as { error?: unknown }).error === 'string';

export const kuwaitDateString = (d: Date): string => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Kuwait' }).format(d);

export const kuwaitDateTimeString = (d: Date | undefined | null): string =>
    d ? new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kuwait', dateStyle: 'medium', timeStyle: 'medium' }).format(d) : '—';

//ponytail: no holiday calendar - mirrors lastBusinessDayString in functions-geneva/src/etf_api.ts
export const lastBusinessDayString = (now: Date = new Date()): string => {
    const weekday = (d: Date) => new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kuwait', weekday: 'short' }).format(d);
    let t = now.getTime() - 24 * 60 * 60 * 1000;
    while (['Fri', 'Sat'].includes(weekday(new Date(t)))) t -= 24 * 60 * 60 * 1000;
    return kuwaitDateString(new Date(t));
};

//webapp-side events (e.g. the Sync NAV click) go to the same audit trail the functions write to
export const logEtfEvent = async (db: Firestore, entry: Omit<EtfApiLog, 'timestamp' | 'id'>) => {
    const record = { ...entry, timestamp: serverTimestamp() };
    try {
        await addDoc(collection(db, 'etf_api_logs'), record);
        await setDoc(doc(db, 'etf_api_status', entry.fund), { lastCalls: { [entry.endpoint]: record } }, { merge: true });
    } catch (e) {
        console.log('logEtfEvent failed', e);
    }
};

//mirrors isPastCutoff in functions-geneva/src/etf_requests.ts (the server re-checks on send): "HH:mm" Kuwait time
export const isPastCutoff = (now: Date, cutoff: string | undefined): boolean => {
    const match = cutoff ? /^(\d{1,2}):(\d{2})$/.exec(cutoff.trim()) : null;
    if (!match) return false;
    const [h, m] = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Kuwait', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
        .format(now).split(':').map(Number);
    return h * 60 + m >= Number(match[1]) * 60 + Number(match[2]);
};

export const navDateOf = (fund: Fund): string | null => (fund.nav_date ? kuwaitDateString(fund.nav_date.toDate()) : null);
export const isNavStale = (fund: Fund): boolean => {
    const navDate = navDateOf(fund);
    return !navDate || navDate < lastBusinessDayString();
};
